import React, { useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import Icon from "@mdi/react";
import { mdiChevronRight } from "@mdi/js";
import { Button, Chip, Table, Tooltip } from "@heroui/react";
import type { SuperadminServiceCenterItemDto } from "@pro-sto/api-client";
import { SkeletonRow } from "@pro-sto/ui-v2";
import { contractorsStore } from "@/stores/ContractorsStore";
import { ROUTES } from "@/constants/routes";
import { highlightText } from "@helpers";
import SortableColumnHeader from "./SortableColumnHeader";
import { formatMoneyRub } from "@shared-utils/helpers";

const STATUS_LABEL: Record<string, string> = {
  active: "Активен",
  hidden: "Скрыт",
};

/** Format a numeric cell value: null/undefined → "—", 0 → "0" */
const fmtNumber = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return String(v);
};

/** Format money: null/undefined → "—", 0 → formatMoney(0) */

interface Column {
  id: string;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  allowsSorting?: boolean;
  render: (
    row: SuperadminServiceCenterItemDto,
    query?: string,
  ) => React.ReactNode;
}

const ContractorsTable: React.FC = observer(() => {
  const navigate = useNavigate();
  const {
    visibleContractors: contractors,
    isLoading,
    isLoadingMore,
    hasNextPage,
    search,
    selectedRows,
    allSelected,
    someSelected,
    sortDescriptor,
  } = contractorsStore;

  // Infinite scroll sentinel
  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelCallback = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isLoadingMore) {
            contractorsStore.loadMore();
          }
        },
        { rootMargin: "200px" },
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isLoadingMore],
  );

  const columns: Column[] = [
    {
      id: "name",
      label: "Название",
      width: "190px",
      render: (row, query) => (
        <Tooltip delay={400}>
          <Tooltip.Trigger>
            <span className="sc-table-cell--truncate sc-table-cell--name">
              {highlightText(row.name, query ?? "")}
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content>{row.name}</Tooltip.Content>
        </Tooltip>
      ),
    },
    {
      id: "address",
      label: "Адрес",
      width: "160px",
      render: (row, query) => (
        <Tooltip delay={400}>
          <Tooltip.Trigger>
            <span className="sc-table-cell--truncate sc-table-cell--address">
              {highlightText(row.address, query ?? "")}
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content>{row.address}</Tooltip.Content>
        </Tooltip>
      ),
    },
    {
      id: "bookingsCount",
      label: "Бронирования",
      width: "110px",
      align: "center",
      allowsSorting: true,
      render: (row) => fmtNumber(row.bookingsCount),
    },
    {
      id: "rating",
      label: "Рейтинг",
      width: "90px",
      align: "center",
      allowsSorting: true,
      render: (row) =>
        row.rating === null || row.rating === undefined
          ? "—"
          : `★ ${row.rating.toFixed(1)}`,
    },
    {
      id: "revenue",
      label: "Доход",
      width: "130px",
      align: "right",
      allowsSorting: true,
      render: (row) => formatMoneyRub(row.revenue),
    },
    {
      id: "commission",
      label: "Комиссия",
      width: "140px",
      align: "right",
      allowsSorting: true,
      render: (row) => formatMoneyRub(row.commission),
    },
    {
      id: "pendingPayout",
      label: "К оплате",
      width: "130px",
      align: "right",
      allowsSorting: true,
      render: (row) => (
        <button
          type="button"
          className={classNames("sc-debt-btn", {
            "sc-debt-btn--positive": row.pendingPayout > 0,
            "sc-debt-btn--zero": row.pendingPayout <= 0,
          })}
          onClick={(e) => {
            e.stopPropagation();
            if (row.pendingPayout > 0) {
              contractorsStore.toggleRow(row.uuid);
            }
          }}
        >
          {row.pendingPayout > 0 ? formatMoneyRub(row.pendingPayout) : "—"}
        </button>
      ),
    },
    {
      id: "status",
      label: "Статус",
      width: "110px",
      render: (row) => (
        <Chip
          color={row.isPublished ? "success" : "warning"}
          variant="soft"
          size="sm"
        >
          {row.isPublished ? STATUS_LABEL.active : STATUS_LABEL.hidden}
        </Chip>
      ),
    },
    {
      id: "actions",
      label: "",
      width: "52px",
      align: "center",
      render: (row) => (
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Открыть"
          data-testid={`contractor-open-button-${row.uuid}`}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            navigate(`${ROUTES.CONTRACTORS}/${row.uuid}`);
          }}
        >
          <Icon path={mdiChevronRight} size={0.9} />
        </Button>
      ),
    },
  ];

  const heroSortDescriptor = {
    column: sortDescriptor.column,
    direction: sortDescriptor.direction,
  };

  const searchLower = search.toLowerCase();
  const isEmpty = !isLoading && contractors.length === 0;

  return (
    <div className="sc-table-wrap" data-testid="contractors-table">
      <Table>
        <Table.ScrollContainer>
          <Table.Content
            aria-label="Сервисные центры"
            className="sc-table"
            style={{ minWidth: "1100px" }}
            sortDescriptor={heroSortDescriptor}
            onSortChange={(desc: {
              column: string | number;
              direction: "ascending" | "descending";
            }) => {
              if (contractorsStore.isUiBusy) return;
              const column = String(desc.column);
              const direction =
                column !== contractorsStore.sortDescriptor.column
                  ? "descending"
                  : desc.direction;
              contractorsStore.setSortDescriptor({ column, direction });
            }}
          >
            <Table.Header className="sc-table__head sticky top-0 z-10">
              <Table.Column
                id="_select"
                className="sc-table__th sc-table__th_checkbox sc-table__th_sticky-left"
                style={{ left: 0 }}
              >
                <input
                  type="checkbox"
                  className="sc-native-checkbox"
                  aria-label="Выбрать все"
                  data-testid="contractors-select-all-checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={() => contractorsStore.toggleAll()}
                  onClick={(e) => e.stopPropagation()}
                />
              </Table.Column>
              {columns.map((col) => (
                <Table.Column
                  key={col.id}
                  id={col.id}
                  isRowHeader={col.id === "name"}
                  allowsSorting={col.allowsSorting}
                  className={classNames("sc-table__th", {
                    "sc-table__th_center": col.align === "center",
                    "sc-table__th_right": col.align === "right",
                    "sc-table__th_sticky-left": col.id === "name",
                    "sc-table__th_sticky-right": col.id === "actions",
                  })}
                  style={
                    col.width
                      ? {
                          width: col.width,
                          ...(col.id === "name" ? { left: "44px" } : {}),
                        }
                      : col.id === "name"
                        ? { left: "44px" }
                        : undefined
                  }
                >
                  {col.allowsSorting
                    ? ({
                        sortDirection,
                      }: {
                        sortDirection?: "ascending" | "descending";
                      }) => (
                        <SortableColumnHeader sortDirection={sortDirection}>
                          {col.label}
                        </SortableColumnHeader>
                      )
                    : col.label}
                </Table.Column>
              ))}
            </Table.Header>
            <Table.Body className="sc-table__body">
              {isLoading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <SkeletonRow key={`skel-${i}`} colCount={columns.length} />
                ))
              ) : contractors.length === 0 ? (
                <Table.Row className="sc-table__row sc-table__row--empty">
                  <Table.Cell
                    className="sc-table__empty-cell"
                    colSpan={columns.length + 1}
                  >
                    {/* Empty state перенесён в overlay; здесь — лишь распорка */}
                    <span aria-hidden="true">&nbsp;</span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                <>
                  {contractors.map((row) => (
                    <Table.Row
                      key={row.uuid}
                      id={row.uuid}
                      data-testid={`contractor-row-${row.uuid}`}
                      className={classNames(
                        "sc-table__row sc-table__row--selectable",
                        {
                          "sc-table__row--selected": selectedRows.has(row.uuid),
                        },
                      )}
                    >
                      <Table.Cell
                        className="sc-table__td sc-table__td_checkbox sc-table__td_sticky-left"
                        style={{ left: 0 }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="sc-native-checkbox"
                          aria-label="Выбрать строку"
                          data-testid={`contractor-row-checkbox-${row.uuid}`}
                          checked={selectedRows.has(row.uuid)}
                          onChange={() => contractorsStore.toggleRow(row.uuid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Table.Cell>
                      {columns.map((col) => (
                        <Table.Cell
                          key={col.id}
                          className={classNames("sc-table__td", {
                            "sc-table__td_center": col.align === "center",
                            "sc-table__td_right": col.align === "right",
                            "sc-table__td_sticky-left": col.id === "name",
                            "sc-table__td_sticky-right": col.id === "actions",
                          })}
                          style={
                            col.id === "name" ? { left: "44px" } : undefined
                          }
                        >
                          {col.render(row, searchLower)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                  {/* Infinite scroll sentinel — пустая строка-наблюдатель.
                    Сам индикатор подгрузки рендерится overlay-ом ниже,
                    чтобы оставаться по центру видимой области. */}
                  {hasNextPage && (
                    <Table.Row
                      ref={sentinelCallback}
                      className="sc-table__row sc-table__row--sentinel"
                      data-testid="contractors-load-more-sentinel"
                    >
                      <Table.Cell
                        className="sc-table__td"
                        colSpan={columns.length + 1}
                      >
                        <span aria-hidden="true">&nbsp;</span>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </>
              )}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
      {isEmpty && (
        <div
          className="sc-table__empty-overlay"
          role="status"
          aria-live="polite"
          data-testid="contractors-empty-state"
        >
          Нет сервисных центров
        </div>
      )}
      {isLoadingMore && (
        <div
          className="sc-table__loading-overlay"
          role="status"
          aria-live="polite"
          data-testid="contractors-loading-more"
        >
          <span className="sc-table__loading-overlay__pill">Загрузка...</span>
        </div>
      )}
    </div>
  );
});

ContractorsTable.displayName = "ContractorsTable";
export default ContractorsTable;
